import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

declare global {
  interface Window {
    google: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class GoogleMapsLoaderService {
  private isLoaded = false;
  private loadingPromise: Promise<void> | null = null;

  constructor() {}

  /**
   * Dynamically loads the Google Maps JavaScript API
   * @returns Promise that resolves when the API is loaded
   */
  loadGoogleMaps(): Promise<void> {
    // If already loaded, return resolved promise
    if (this.isLoaded) {
      return Promise.resolve();
    }

    // If currently loading, return the existing promise
    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    // Create new loading promise
    this.loadingPromise = new Promise<void>((resolve, reject) => {
      // Check if Google Maps is already available (in case script was loaded elsewhere)
      if (typeof (window as any).google !== 'undefined' && (window as any).google.maps) {
        this.isLoaded = true;
        resolve();
        return;
      }

      // Create script element
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}`;

      // Handle successful load
      script.onload = () => {
        this.isLoaded = true;
        resolve();
      };

      // Handle load error
      script.onerror = (error) => {
        console.error('Failed to load Google Maps API:', error);
        reject(new Error('Failed to load Google Maps API'));
      };

      // Append script to document head
      document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  /**
   * Check if Google Maps API is loaded
   * @returns boolean indicating if the API is loaded
   */
  isGoogleMapsLoaded(): boolean {
    return this.isLoaded && typeof (window as any).google !== 'undefined' && !!(window as any).google.maps;
  }
}
